/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import { request } from '@mairistem/tools';
import { App, Feature, Menu, render } from '@mairistem/module';
import { Segment, Icon, Tooltip } from '@mairistem/composants';

import { Context, AssistantContextProvider } from '@/components/Context';
import { ModalToastContainer } from '@/components/Common';
import { Dashboard } from '@/components/Dashboard';
import { Assistant } from '@/components/Assistant';

import { useEntiteStore } from './zustand';

import { initialize } from './sentry';

import { ActeFicheContainer } from './components/Acte/Fiche';
import { ActeDocumentsRoute } from './components/Acte/Documents';
import { ActeNaissanceListeContainer } from './components/Acte/ListeNaissance';
import { ActeReconnaissanceListeContainer } from './components/Acte/ListeReconnaissance';
import { ActeDecesListeContainer } from './components/Acte/ListeDeces';
import { ActeMariageListeContainer } from './components/Acte/ListeMariage';

import './index.less';
import 'react-toastify/dist/ReactToastify.css';
import { Baby, Cross, HeartHandshake, UserCheck } from 'lucide-react';

initialize();

request.interceptors.request.use((config) => {
  if (process.env.NODE_ENV === 'production') {
    if (_.includes(window.location.href, '.jvsonline.fr')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.jvsonline.fr';
    } else if (_.includes(window.location.href, '.qa.')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.qa.jvs-mairistem.dev';
    } else if (_.includes(window.location.href, '.pp.')) {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.pp.jvs-mairistem.dev';
    } else {
      // eslint-disable-next-line no-param-reassign
      config.baseURL = 'https://360.jvs-mairistem.dev';
    }
  } else {
    // eslint-disable-next-line no-param-reassign
    config.headers['X-Database'] = process.env.X_DATABASE;
    // eslint-disable-next-line no-param-reassign
    config.headers['X-Login'] = 1;
  }

  return config;
});

const EntiteHandler = () => {
  const setRefresh = useEntiteStore((state) => state.setRefresh);

  React.useEffect(() => {
    setRefresh(true);
  }, [setRefresh]);

  return <div />;
};

const code = '360_NUMA';
const showTooltips = !window.location.href.includes('360');

// Ne pas modifier ou supprimer
// Utile en mode production afin que le style se place au bonne endroit
if (process.env.NODE_ENV !== 'development') {
  const oldCreatePortal = ReactDOM.createPortal;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ReactDOM.createPortal = (child, domNode) =>
    oldCreatePortal(
      child,
      domNode === document.body
        ? document.querySelector(`#${CSS.escape(code)}`) || domNode
        : domNode || document.querySelector(`#${CSS.escape(code)}`),
    );
}

render(
  <App
    group="administres"
    name="Numérisation Actes"
    code={code}
    request={request}
    context={Context}
    additionalProductCodesUpdate={['HOL_ETCV', '360_GAN']}
  >
    <Menu
      name="Entités"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="building" />}
            message="Entités"
            position="right center"
          />
        ) : (
          <Icon name="building" />
        )
      }
      path="/entites"
    >
      <Feature>
        <Feature.Content>
          <>
            <ModalToastContainer />
            <EntiteHandler />
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Accueil"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="home" />}
            message="Accueil"
            position="right center"
          />
        ) : (
          <Icon name="home" />
        )
      }
      path="/"
      exact
    >
      <Feature>
        <Feature.Content>
          <>
            <ModalToastContainer />
            <Segment
              basic
              secondary
              style={{ margin: 0, height: '100%', padding: 0 }}
              className="dashboard-segment"
            >
              <Dashboard />
            </Segment>
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Paramètres"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Icon name="settings" />}
            message="Paramètres"
            position="right center"
          />
        ) : (
          <Icon name="settings" />
        )
      }
      path="/parametres"
    >
      <Feature>
        <Feature.Content>
          <>
            <ModalToastContainer />
            Paramètres
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu.Separator />
    <Menu
      name="Acte de naissance"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Baby />}
            message="Acte de naissance"
            position="right center"
          />
        ) : (
          <Baby />
        )
      }
      path="/naissance"
    >
      <Feature>
        <Feature.Content>
          <Segment basic secondary style={{ margin: 0, height: '100%' }}>
            <Segment
              raised
              style={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
              }}
            >
              <ActeNaissanceListeContainer />
            </Segment>
          </Segment>
        </Feature.Content>
      </Feature>
      <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
        <Feature.Content>
          <ActeFicheContainer typeActe="naissance" />
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Acte de reconnaissance"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<UserCheck />}
            message="Acte de reconnaissance"
            position="right center"
          />
        ) : (
          <UserCheck />
        )
      }
      path="/reconnaissance"
    >
      <Feature>
        <Feature.Content>
          <Segment basic secondary style={{ margin: 0, height: '100%' }}>
            <Segment
              raised
              style={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
              }}
            >
              <ActeReconnaissanceListeContainer />
            </Segment>
          </Segment>
        </Feature.Content>
      </Feature>
      <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
        <Feature.Content>
          <ActeFicheContainer typeActe="reconnaissance" />
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Acte de mariage"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<HeartHandshake />}
            message="Acte de mariage"
            position="right center"
          />
        ) : (
          <HeartHandshake />
        )
      }
      path="/mariage"
    >
      <Feature>
        <Feature.Content>
          <Segment basic secondary style={{ margin: 0, height: '100%' }}>
            <Segment
              raised
              style={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
              }}
            >
              <ActeMariageListeContainer />
            </Segment>
          </Segment>
        </Feature.Content>
      </Feature>
      <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
        <Feature.Content>
          <ActeFicheContainer typeActe="mariage" />
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu
      name="Acte de décès"
      icon={
        showTooltips ? (
          <Tooltip
            trigger={<Cross />}
            message="Acte de décès"
            position="right center"
          />
        ) : (
          <Cross />
        )
      }
      path="/deces"
    >
      <Feature>
        <Feature.Content>
          <Segment basic secondary style={{ margin: 0, height: '100%' }}>
            <Segment
              raised
              style={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
              }}
            >
              <ActeDecesListeContainer />
            </Segment>
          </Segment>
        </Feature.Content>
      </Feature>
      <Feature path="/:identifiant(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})">
        <Feature.Content>
          <ActeFicheContainer typeActe="deces" />
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu path="/documents" exact>
      <Feature path="/documents/:typeActe(naissance|reconnaissance|deces|mariage)/:identifiant(\d+)">
        <Feature.Content>
          <>
            <ModalToastContainer />
            <ActeDocumentsRoute />
          </>
        </Feature.Content>
      </Feature>
    </Menu>
    <Menu path="/assistant" exact>
      <Feature path="/assistant">
        <Feature.Content>
          <>
            <ModalToastContainer />
            <AssistantContextProvider>
              <Assistant />
            </AssistantContextProvider>
          </>
        </Feature.Content>
      </Feature>
    </Menu>
  </App>,
);
